import React from 'react';
import { useRecoilValue } from 'recoil';
import { discountForDisplay, getActiveDiscount } from '../../discounts';
import { Fit } from '../../enums/Fit.enum';
import { ProductHandle } from '../../enums/ProductHandle.enum';
import { Icon } from '../../icons/Icon';
import { ShopifyCart } from '../../models/ShopifyCart.model';
import { ShopifyLineItem } from '../../models/ShopifyLineItem.model';
import { ViewProduct } from '../../models/ViewProduct.model';
import { LongSize } from '../../product_detail/components/size.enum';
import { environmentContextState, shopifyState, toViewPriceFnState, abTestState } from '../../recoil/atoms';
import { productListState } from '../../recoil/selectors';
import { uniqueArray } from '../../util';
import { cartGarmentCount } from '../CartHelper';
import { CartPromoColorItem } from './CartPromoColorItem';
import { CartPromoItem } from './CartPromoItem';
import classNames from 'classnames';
import styles from './CartPromoList.module.scss';

interface Props {
  cart: ShopifyCart | undefined;
  forceItemUpsell?: boolean;
  title?: string;
}

export const CartPromoList: React.FC<Props> = ({ cart, forceItemUpsell = false, title }) => {
  const garmentCount = cartGarmentCount(cart);
  const { timedSaleActive } = useRecoilValue(environmentContextState);
  const nextDiscount = getActiveDiscount(garmentCount + 1, timedSaleActive);
  const shopify = useRecoilValue(shopifyState);
  const abTests = useRecoilValue(abTestState);
  const toViewPriceFn = useRecoilValue(toViewPriceFnState);
  const displayDiscount = discountForDisplay(2, toViewPriceFn, timedSaleActive, shopify);

  const productHandlesInCart = uniqueArray((cart?.lineItems ?? []).map((li) => li.handle));
  const productList = useRecoilValue(productListState);
  const filteredProducts: ViewProduct[] = getProductRecommendationsFromCartProduct(cart?.lineItems, productList);
  const colorPromoProduct = productList.find((p) => productHandlesInCart.includes(p.handle));
  const colorPromoVariant = colorPromoProduct && colorPromoProduct.variants.find((v) => v.sku === cart?.lineItems[0].sku);

  // only show the color promo when there is one product with more than one color in the cart
  const showColorUpsellPromo = colorPromoProduct && colorPromoProduct.colors.length > 1 && productHandlesInCart.length === 1;
  const showItemUpsellPromo = (forceItemUpsell || productHandlesInCart.length > 1) && filteredProducts.length > 0;

  const sectionTitle = title ? (
    title
  ) : garmentCount > 0 ? (
    <span>
      Get {displayDiscount} off {garmentCount === 1 ? 'a second' : 'another'} garment{showItemUpsellPromo && <sup>*</sup>}
    </span>
  ) : (
    <span>Popular garments</span>
  );
  return (
    <>
      {(showItemUpsellPromo || showColorUpsellPromo) && (
        <div className={classNames(styles.promoSectionTitle, { [styles.small]: abTests['AB2022-09'] })}>
          <Icon name="badge-percent" style={{ width: '26px', marginBottom: '-8px', marginRight: '6px' }} />
          {sectionTitle}
        </div>
      )}
      {showColorUpsellPromo && !showItemUpsellPromo && (
        <CartPromoColorItem
          key={productHandlesInCart[0] + '-color-promo'}
          selectionKey={productHandlesInCart[0]}
          product={colorPromoProduct}
          initialSize={(colorPromoVariant?.size as LongSize) ?? LongSize.Medium}
          initialFit={(colorPromoVariant?.fit as Fit) ?? Fit.Regular}
          discount={nextDiscount}
        />
      )}
      {showItemUpsellPromo && filteredProducts.map((p) => <CartPromoItem key={p.handle} selectionKey={p.handle} product={p} discount={nextDiscount} />)}
      {showItemUpsellPromo && (
        <div className={styles.promoAsterisk}>
          <sup>*</sup>The {displayDiscount} discount is applied to the item(s) of lesser value in your cart
        </div>
      )}
    </>
  );
};

export const getProductRecommendationsFromCartProduct = (cartLineItems: ShopifyLineItem[] = [], productList: ViewProduct[]): ViewProduct[] => {
  const handlesInCart = Array.from(new Set(cartLineItems.map((cli) => cli.handle)));

  const productListByHandle: {
    [key: string]: ProductHandle[];
  } = {};

  handlesInCart.forEach((handleInCart) => {
    productListByHandle[handleInCart] = getHandleRecommendationsFromHandle(handleInCart);
  });

  const rankedProductHandles: ProductHandle[] = [];
  [0, 1, 2, 3, 4, 5, 6].forEach((index) => {
    handlesInCart.forEach((handleInCart) => {
      rankedProductHandles.push(productListByHandle[handleInCart][index]);
    });
  });
  const uniqueRankedProductHandles = Array.from(new Set(rankedProductHandles.filter((h) => !handlesInCart.includes(h)))).filter((p) => !!p);
  return uniqueRankedProductHandles.slice(0, 6).map((handle) => productList.find((p) => p.handle === handle)) as ViewProduct[];
};

// eslint-disable-next-line max-lines-per-function
const getHandleRecommendationsFromHandle = (handle: ProductHandle): ProductHandle[] => {
  switch (handle) {
    case ProductHandle.BoldnessBodysuit:
      return [
        ProductHandle.SilhouetteBrief,
        ProductHandle.TankBodysuit,
        ProductHandle.LiftWearCami,
        ProductHandle.SilhouetteBra,
        ProductHandle.CamiBodysuit,
        ProductHandle.SuperPowerShort,
        ProductHandle.LiftWearTank,
      ];
    case ProductHandle.BoldnessTank:
      return [
        ProductHandle.LiftWearCami,
        ProductHandle.LiftWearTank,
        ProductHandle.VNeckBra,
        ProductHandle.SilhouetteBrief,
        ProductHandle.CrossoverBra,
        ProductHandle.SuperPowerShort,
        ProductHandle.BoldnessBodysuit,
      ];
    case ProductHandle.CamiBodysuit:
      return [
        ProductHandle.SilhouetteBra,
        ProductHandle.LiftWearCami,
        ProductHandle.SilhouetteBrief,
        ProductHandle.CrossoverBra,
        ProductHandle.VNeckBra,
        ProductHandle.SuperPowerShort,
        ProductHandle.SuperPowerBrief,
      ];
    case ProductHandle.CrossoverBra:
      return [
        ProductHandle.SilhouetteBra,
        ProductHandle.VNeckBra,
        ProductHandle.SilhouetteBrief,
        ProductHandle.LiftWearCami,
        ProductHandle.LiftWearTank,
        ProductHandle.SuperPowerShort,
        ProductHandle.SuperPowerBrief,
      ];

    case ProductHandle.EverReadyPant:
      return [
        ProductHandle.SilhouetteBrief,
        ProductHandle.LiftWearCami,
        ProductHandle.SilhouetteBra,
        ProductHandle.LeggingTwo,
        ProductHandle.LeggingTwo,
        ProductHandle.LiftWearTank,
        ProductHandle.CrossoverBra,
      ];

    case ProductHandle.LeggingTwo:
      return [
        ProductHandle.SilhouetteBrief,
        ProductHandle.VNeckBra,
        ProductHandle.LiftWearTank,
        ProductHandle.LiftWearTank,
        ProductHandle.LiftWearCami,
        ProductHandle.SuperPowerShort,
        ProductHandle.CrossoverBra,
      ];

    case ProductHandle.LiftWearCami:
      return [
        ProductHandle.CrossoverBra,
        ProductHandle.VNeckBra,
        ProductHandle.LiftWearTank,
        ProductHandle.SilhouetteBra,
        ProductHandle.SilhouetteBrief,
        ProductHandle.SuperPowerShort,
        ProductHandle.SuperPowerBrief,
      ];

    case ProductHandle.LiftWearTank:
      return [
        ProductHandle.VNeckBra,
        ProductHandle.LiftWearCami,
        ProductHandle.CrossoverBra,
        ProductHandle.SilhouetteBrief,
        ProductHandle.SilhouetteBra,
        ProductHandle.SuperPowerShort,
        ProductHandle.TankBodysuit,
      ];

    case ProductHandle.QueenBrief:
      return [
        ProductHandle.SuperPowerShort,
        ProductHandle.SilhouetteBrief,
        ProductHandle.SuperPowerBrief,
        ProductHandle.SilhouetteBra,
        ProductHandle.CrossoverBra,
        ProductHandle.LiftWearCami,
        ProductHandle.VNeckBra,
      ];

    case ProductHandle.SilhouetteBra:
      return [
        ProductHandle.SilhouetteBrief,
        ProductHandle.CrossoverBra,
        ProductHandle.VNeckBra,
        ProductHandle.LiftWearCami,
        ProductHandle.SuperPowerShort,
        ProductHandle.LiftWearTank,
        ProductHandle.SuperPowerBrief,
      ];
    case ProductHandle.SilhouetteBrief:
      return [
        ProductHandle.SilhouetteBra,
        ProductHandle.CrossoverBra,
        ProductHandle.VNeckBra,
        ProductHandle.LiftWearCami,
        ProductHandle.LiftWearTank,
        ProductHandle.SuperPowerShort,
        ProductHandle.QueenBrief,
      ];
    case ProductHandle.SuperPowerBrief:
      return [
        ProductHandle.SuperPowerBrief,
        ProductHandle.SilhouetteBrief,
        ProductHandle.CrossoverBra,
        ProductHandle.LiftWearCami,
        ProductHandle.VNeckBra,
        ProductHandle.SilhouetteBra,
        ProductHandle.QueenBrief,
      ];
    case ProductHandle.SuperPowerShort:
      return [
        ProductHandle.SuperPowerBrief,
        ProductHandle.SilhouetteBrief,
        ProductHandle.CrossoverBra,
        ProductHandle.LiftWearCami,
        ProductHandle.VNeckBra,
        ProductHandle.SilhouetteBra,
        ProductHandle.QueenBrief,
      ];
    case ProductHandle.SuperPowerThong:
      return [
        ProductHandle.VNeckBra,
        ProductHandle.SuperPowerBrief,
        ProductHandle.SuperPowerShort,
        ProductHandle.LiftWearCami,
        ProductHandle.CamiBodysuit,
        ProductHandle.QueenBrief,
        ProductHandle.LiftWearTank,
      ];
    case ProductHandle.TankBodysuit:
      return [
        ProductHandle.CamiBodysuit,
        ProductHandle.SilhouetteBrief,
        ProductHandle.LiftWearCami,
        ProductHandle.LiftWearTank,
        ProductHandle.CrossoverBra,
        ProductHandle.BoldnessBodysuit,
        ProductHandle.BoldnessBodysuit, // ERROR IN EXCEL?
      ];
    case ProductHandle.VNeckBra:
      return [
        ProductHandle.CrossoverBra,
        ProductHandle.SilhouetteBra,
        ProductHandle.LiftWearTank,
        ProductHandle.LiftWearCami,
        ProductHandle.SilhouetteBrief,
        ProductHandle.SuperPowerShort,
        ProductHandle.CamiBodysuit,
      ];
    case ProductHandle.WaistHeroCincher:
      return [
        ProductHandle.LiftWearCami,
        ProductHandle.SuperPowerShort,
        ProductHandle.SilhouetteBrief,
        ProductHandle.BoldnessTank,
        ProductHandle.CamiBodysuit,
        ProductHandle.QueenBrief,
        ProductHandle.LiftWearTank,
      ];
    default:
      return [];
  }
};

export default CartPromoList;
